import { isPlainObject, toEntries } from '@spazious/dollhouse-utils';

import { ParentScope, Scope, ScopeRecord, ScopeRecordEntries } from './types.js';
import { formatVar } from './utils.js';

export class ScopeImpl<
    TRecord extends ScopeRecord = ScopeRecord,
    TParent extends ParentScope = never
  >
  extends Map<string, any>
  implements Scope
{
  public name: string;
  public parent: TParent | null = null;

  constructor(record?: TRecord, name?: string, parent?: TParent);
  constructor(
    entries?: IterableIterator<ScopeRecordEntries<TRecord>>,
    name?: string,
    parent?: TParent
  );
  constructor(
    mixed?: TRecord | IterableIterator<ScopeRecordEntries<TRecord>>,
    name = 'root',
    parent?: TParent
  ) {
    super();
    this.name = name;
    this.parent = parent ?? null;
    if (mixed) {
      this.set(mixed);
    }
  }

  keys(): IterableIterator<any> {
    return super.keys();
  }

  get(key: string, bubble = true): any {
    const keyvar = formatVar(key);
    if (super.has(keyvar)) {
      return super.get(keyvar);
    }

    if (bubble && this.parent) {
      return this.parent.get(key);
    }

    return undefined;
  }

  has(key: string, bubble = true): boolean {
    return super.has(formatVar(key)) || ((bubble && this.parent?.has(key)) ?? false);
  }

  /**
   * Set a value in the scope.
   */
  set(entries: IterableIterator<[string, any]>): this;
  set(record: Record<string, any>): this;
  set(key: string, value: any): this;
  set(mixed: string | Record<string, any> | IterableIterator<[string, any]>, value?: any): this {
    if (typeof mixed === 'string') {
      super.set(mixed, this.nestScope(mixed, value));
    } else {
      toEntries(mixed).forEach(([key, value]) => super.set(key, this.nestScope(key, value)));
    }

    return this;
  }

  protected parseValue(key: string, value: any) {
    return value;
  }

  /**
   * This function is called when a value is set in the scope and
   * the returned value has to be set as scope.
   */
  protected nestScope(key: string, value: any) {
    value = this.parseValue(key, value);
    value = isPlainObject(value) ? new ScopeImpl(value) : value;

    if (value instanceof ScopeImpl) {
      value.parent = this;
      value.name = `${this.name}.${key.toString()}`;
    }

    return value;
  }

  /** internal function */
  createSubScope() {
    return this;
  }

  toJSON() {
    return Object.fromEntries(this.entries());
  }
}

export const createScope = <
  TRecord extends ScopeRecord = ScopeRecord,
  TParent extends ParentScope = never
>(
  mixed?: TRecord | IterableIterator<ScopeRecordEntries<TRecord>>,
  path = 'root',
  parent?: TParent
) => new ScopeImpl(mixed, path, parent);
